/**
 * Map attention-queue rows to Gantt week ranges using status date (not synthetic far-future indices).
 */

import type { ControlBridgeQueueItem } from '../deriveViewModel';
import { proxyWeekRangeFromIndex, statusFromPhrase } from './derivation';
import { guidanceTodayWeek, isoDateToWeekIndex } from './guidanceViewport';
import type { GuidanceTreeNode, GuidanceWeekRange } from './types';

/** Proxy axis "today" — aligned with ``guidanceTodayWeek()``. */
export const GUIDANCE_STATUS_DATE_FALLBACK_WEEK = guidanceTodayWeek();

const DEFAULT_ACTIVITY_SPAN = 1;
const DEFAULT_ACTIVITY_SLACK = 0.5;

function queueStatusDateIso(item: ControlBridgeQueueItem): string | undefined {
  if (typeof item.statusDate === 'string' && item.statusDate.trim()) {
    return item.statusDate.trim().slice(0, 10);
  }
  const raw = item as ControlBridgeQueueItem & Record<string, unknown>;
  const snake = raw['status_date'];
  if (typeof snake === 'string' && snake.trim()) {
    return snake.trim().slice(0, 10);
  }
  return undefined;
}

/** Week index where an eligible activity's bar should start. */
export function queueItemStatusWeek(item: ControlBridgeQueueItem, fallbackIndex?: number): number {
  const iso = queueStatusDateIso(item);
  if (iso) {
    return isoDateToWeekIndex(iso);
  }
  if (typeof item.timelineIndex === 'number' && Number.isFinite(item.timelineIndex)) {
    return item.timelineIndex * DEFAULT_ACTIVITY_SPAN;
  }
  if (fallbackIndex !== undefined) {
    return fallbackIndex * DEFAULT_ACTIVITY_SPAN;
  }
  return GUIDANCE_STATUS_DATE_FALLBACK_WEEK;
}

export function weekRangeFromQueueItem(
  item: ControlBridgeQueueItem,
  fallbackIndex?: number,
  span = DEFAULT_ACTIVITY_SPAN,
  slack = DEFAULT_ACTIVITY_SLACK
): GuidanceWeekRange {
  const s = queueItemStatusWeek(item, fallbackIndex);
  return { s, e: s + span, l: s + span + slack };
}

/** Parent bar spanning injected / grouped child activity ranges. */
export function envelopeWeekRange(ranges: GuidanceWeekRange[]): GuidanceWeekRange {
  if (ranges.length === 0) {
    return proxyWeekRangeFromIndex(Math.floor(GUIDANCE_STATUS_DATE_FALLBACK_WEEK), 2, 1);
  }
  const s = Math.min(...ranges.map((r) => r.s));
  const l = Math.max(...ranges.map((r) => r.l));
  const e = Math.max(s + 0.25, Math.min(...ranges.map((r) => r.e)));
  return { s, e, l };
}

function activityNodeFromQueueItem(
  node: GuidanceTreeNode,
  item: ControlBridgeQueueItem
): GuidanceTreeNode {
  const status = statusFromPhrase(item.statusPhrase);
  return {
    ...node,
    label: item.title || node.label,
    status,
    range: weekRangeFromQueueItem(item),
    pct: status === 'active' ? 0.55 : node.pct ?? 0,
    res: item.assignedResourceLabel ?? node.res,
  };
}

/**
 * Reschedule every activity in the attention eligible queue to its status date (or today).
 * Re-envelopes parent improvement/waypoint bars when children move.
 */
export function applyEligibleQueueScheduling(
  root: GuidanceTreeNode,
  eligibleWork: ControlBridgeQueueItem[] = []
): GuidanceTreeNode {
  const byId = new Map(
    eligibleWork.filter((item) => item.itemId).map((item) => [item.itemId, item])
  );
  if (byId.size === 0) {
    return root;
  }

  const walk = (node: GuidanceTreeNode, parent?: GuidanceTreeNode): GuidanceTreeNode => {
    const parentHasPlankDates =
      parent?.type === 'improvement' && Boolean(parent.startDate?.trim());
    const skipReschedule =
      parentHasPlankDates || (node.type === 'improvement' && Boolean(node.startDate?.trim()));
    const queueItem =
      node.type === 'activity' && !skipReschedule ? byId.get(node.id) : undefined;
    if (queueItem) {
      node = activityNodeFromQueueItem(node, queueItem);
    }

    const kids = (node.children ?? []).map((child) => walk(child, node));
    if (kids.length === 0) {
      return node;
    }

    const childRescheduled = kids.some((child) => {
      if (child.type !== 'activity' || !byId.has(child.id)) {
        return false;
      }
      if (node.type === 'improvement' && node.startDate?.trim()) {
        return false;
      }
      return true;
    });
    const range =
      childRescheduled && (node.type === 'improvement' || node.type === 'waypoint')
        ? envelopeWeekRange(kids.map((c) => c.range))
        : node.range;

    return { ...node, range, children: kids };
  };

  return walk(root);
}
